/**
 * 学ぶ（LEARN）／記事（LEARN ARTICLE）
 *
 * - 学ぶ：検索（全章の全文索引）、タイル（ゾーン早見表・マイハイライト・AI に質問）、31 章の目次
 *   検索 0 件のときは AI へ誘導（検索語をそのまま送る）
 * - 記事：明朝の本文、図・表、02 章はゾーン早見表を埋め込み。文字を選ぶと黒い帯でハイライト（黄／赤）
 *   右上：マイハイライト・印刷。下端：前の章・目次・次の章
 *
 * 本文 HTML は固定の教材データ（data/learn-content.js）なので innerHTML で挿入する。
 * 検索語・ハイライトなど外部由来の文字は必ずエスケープする。
 */

import { showScreen, escapeHtml, toast, icon, barHtml, backBtn, zoneCellsHtml, ZONE_ROMAN, ZONE_NAMES, ZONE_EXAMPLES } from './ui.js';
import { ARTICLES, getArticle } from './data/learn-content.js';
import { search, highlightHtml } from './search.js';
import { injectHighlights, bindSelectionToolbar, openMyHighlights, loadHighlights } from './learn-highlights.js';

let query = '';

export function openLearnIndex() {
  renderIndex();
  showScreen('learn');
}

function renderIndex() {
  const el = document.getElementById('screen-learn');
  el.innerHTML = `
    ${barHtml(backBtn(), `<button class="bar__text" type="button" data-ask style="font-size:13px">${icon('chat', 18)}AI に質問</button>`)}
    <div class="page-head"><div><div class="eyebrow">learn</div><div class="page-title">学ぶ</div></div></div>
    <label class="search" id="learn-search">${icon('search', 18)}<input type="search" placeholder="教材を検索（例：相反則、N-1、赤フィルター）" autocomplete="off" enterkeyhint="search" value="${escapeHtml(query)}" /></label>
    <div class="body" id="learn-body"></div>
  `;
  el.querySelector('[data-ask]').addEventListener('click', () => openAskFrom(''));
  const input = el.querySelector('#learn-search input');
  let t = null;
  input.addEventListener('input', () => {
    query = input.value;
    clearTimeout(t);
    t = setTimeout(renderList, 120);
  });
  renderList();
}

function renderList() {
  const body = document.getElementById('learn-body');
  const q = query.trim();
  document.getElementById('learn-search')?.classList.toggle('is-active', !!q);
  if (!q) {
    body.innerHTML = `
      <div class="learn-tiles">
        <button class="ltile" type="button" data-zc><span class="zstrip">${zoneCellsHtml()}</span><span style="font:500 13px/1.2 var(--font-jp-sans)">ゾーン早見表</span></button>
        <button class="ltile" type="button" data-hl style="animation-delay:.06s"><span style="font:300 20px/1 var(--font-en)">${loadHighlights().length}</span><span style="font:500 13px/1.2 var(--font-jp-sans)">マイハイライト</span></button>
        <button class="ltile ltile--solid" type="button" data-ask style="animation-delay:.12s">${icon('chat', 18)}<span style="font:500 13px/1.2 var(--font-jp-sans)">AI に質問</span></button>
      </div>
      <div class="list-head"><span class="eyebrow">contents</span><span class="sub" style="font-size:12px">${ARTICLES.length} 章</span></div>
      ${ARTICLES.map((a, k) => `
        <button class="chap" type="button" data-article="${escapeHtml(a.id)}" style="animation-delay:${Math.min(k, 10) * 30}ms">
          <span class="chap__n">${escapeHtml(a.chapter)}</span>
          <div style="flex:1;min-width:0"><div class="chap__t">${escapeHtml(a.title)}</div><div class="chap__s">${escapeHtml(a.summary)}</div></div>
        </button>`).join('')}
      <div style="height:40px"></div>`;
    body.querySelector('[data-zc]').addEventListener('click', () => import('./zone-chart.js').then((m) => m.openZoneChart()));
    body.querySelector('[data-hl]').addEventListener('click', () => openMyHighlights());
    body.querySelector('[data-ask]').addEventListener('click', () => openAskFrom(''));
  } else {
    const results = search(q, 30);
    body.innerHTML = `
      <div class="list-head"><span class="eyebrow">results</span><span class="sub" style="font-size:12px">${results.length} 件</span></div>
      ${results.map((r, k) => `
        <button class="chap" type="button" data-article="${escapeHtml(r.articleId)}" style="animation-delay:${Math.min(k, 10) * 30}ms">
          <span class="chap__n">${escapeHtml(r.chapter)}</span>
          <div style="flex:1;min-width:0"><div class="chap__t">${highlightHtml(r.title, q)}<span class="sub" style="font-size:12px"> · ${highlightHtml(r.sectionHeading, q)}</span></div><div class="chap__s">${highlightHtml(r.snippet, q)}</div></div>
        </button>`).join('')}
      ${results.length === 0 ? `
        <div style="margin:24px var(--gutter) 0;animation:zn-up .4s both">
          <div style="font:500 20px/1.4 var(--font-jp-serif)">該当が見つかりませんでした</div>
          <p class="sub" style="margin:8px 0 0;font-size:14px;line-height:1.75">言い換えて検索するか、教材を読み込んだ AI に聞いてみてください。</p>
          <button class="ai-cta" type="button" data-ask-q>${icon('chat', 18)}<div style="flex:1"><div style="font:500 15px/1.3 var(--font-jp-sans)">AI に質問する</div><div style="font:400 12px/1.5 var(--font-jp-sans);opacity:.75;margin-top:2px">検索語をそのまま送ります</div></div></button>
        </div>` : ''}
      <div style="height:40px"></div>`;
    body.querySelector('[data-ask-q]')?.addEventListener('click', () => openAskFrom(q));
  }
  body.querySelectorAll('[data-article]').forEach((b) => b.addEventListener('click', () => openLearnArticle(b.dataset.article)));
}

function openAskFrom(q) {
  import('./ask.js').then((m) => m.openAsk({ question: q }));
}

// --------------------------------------------------
// 記事
// --------------------------------------------------
export function openLearnArticle(id, opts = {}) {
  const article = getArticle(id);
  if (!article) { toast('記事が見つかりません'); return; }
  renderArticle(article);
  showScreen('learn-article', { back: !!opts.back });
}

function renderArticle(article) {
  const el = document.getElementById('screen-learn-article');
  const idx = ARTICLES.findIndex((a) => a.id === article.id);
  const prev = idx > 0 ? ARTICLES[idx - 1] : null;
  const next = idx < ARTICLES.length - 1 ? ARTICLES[idx + 1] : null;
  el.innerHTML = `
    ${barHtml(backBtn(), `<div style="display:flex"><button class="bar__btn" type="button" data-hl aria-label="マイハイライト">${icon('list')}</button><button class="bar__btn" type="button" data-print aria-label="印刷">${icon('print')}</button></div>`)}
    <div class="body" id="art-body">
      <div class="article">
        <div class="article-chapter">CHAPTER ${escapeHtml(article.chapter)}</div>
        <h2 class="article-title">${escapeHtml(article.title)}</h2>
        <p class="article-summary">${escapeHtml(article.summary)}</p>
        ${article.showZoneChart ? embeddedZoneChart() : ''}
        ${article.sections.map((s) => `
          <section class="article-section">
            <h3 class="article-heading">${escapeHtml(s.heading)}</h3>
            <div class="article-body">${injectHighlights(s.body, article.id)}</div>
          </section>`).join('')}
        <p class="sub" style="font-size:11px;line-height:1.6;margin-top:14px">文字を長押しして選ぶと、黄／赤のハイライトを付けられます。</p>
      </div>
    </div>
    <div class="art-nav">
      <button type="button" data-nav="${prev ? escapeHtml(prev.id) : ''}" ${prev ? '' : 'disabled'}><span class="art-nav__n">${prev ? escapeHtml(prev.chapter) : ''}</span><span class="art-nav__t">${prev ? escapeHtml(prev.title) : '—'}</span></button>
      <button type="button" data-toc>目次</button>
      <button type="button" data-nav="${next ? escapeHtml(next.id) : ''}" ${next ? '' : 'disabled'}><span class="art-nav__n">${next ? escapeHtml(next.chapter) : ''}</span><span class="art-nav__t">${next ? escapeHtml(next.title) : '—'}</span></button>
    </div>
  `;
  el.querySelector('[data-hl]').addEventListener('click', () => openMyHighlights());
  el.querySelector('[data-print]').addEventListener('click', () => {
    document.body.classList.add('print-article');
    window.print();
    setTimeout(() => document.body.classList.remove('print-article'), 1000);
  });
  el.querySelectorAll('[data-nav]').forEach((b) => b.addEventListener('click', () => {
    if (!b.dataset.nav) return;
    const back = ARTICLES.findIndex((a) => a.id === b.dataset.nav) < idx;
    openLearnArticle(b.dataset.nav, { back });
  }));
  el.querySelector('[data-toc]').addEventListener('click', () => { renderIndex(); showScreen('learn', { back: true }); });
  bindSelectionToolbar(el.querySelector('#art-body'), article.id, article.title);
}

function embeddedZoneChart() {
  return `
    <div style="margin-top:24px">
      <div class="eyebrow" style="padding-bottom:10px;border-bottom:1px solid var(--color-ink)">zone chart</div>
      ${ZONE_ROMAN.map((r, i) => `
        <div style="display:flex;align-items:center;gap:12px;min-height:40px;padding:4px 0;border-bottom:1px solid var(--color-rule)">
          <span class="zc-${i}" style="width:40px;height:28px;flex:none;box-shadow:inset 0 0 0 1px var(--color-rule)"></span>
          <span style="width:34px;font:600 13px/1 var(--font-en)">${r}</span>
          <span style="flex:1;font:400 13px/1.4 var(--font-jp-sans)">${escapeHtml(ZONE_NAMES[i])}<span class="sub" style="font-size:12px"> — ${escapeHtml(ZONE_EXAMPLES[i])}</span></span>
        </div>`).join('')}
    </div>`;
}
